/* eslint-disable require-jsdoc, max-len */
// Pure CommonJS domain rules shared by the browser, server and maintenance tools.
// Group rounds are the only execution count. Reading legacy data never writes it.
const MAX_GROUP_ROUNDS = 30;
const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

function groupRoundCount(value) {
  if (typeof value === "number") return Number.isInteger(value) && value >= 1 && value <= MAX_GROUP_ROUNDS ? value : null;
  if (typeof value !== "string" || !/^\d+(?:[.,]0+)?$/.test(value.trim())) return null;
  const rounds = Number(value.trim().replace(",", "."));
  return Number.isInteger(rounds) && rounds >= 1 && rounds <= MAX_GROUP_ROUNDS ? rounds : null;
}

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (!record(value)) return value;
  return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]));
}

function samePrescriptions(sets) {
  if (!Array.isArray(sets) || !sets.length || sets.some((set) => !record(set))) return false;
  const signatures = sets.map((set) => {
    const prescription = {...set};
    delete prescription.id;
    return JSON.stringify(canonical(prescription));
  });
  return signatures.every((signature) => signature === signatures[0]);
}

function groupIssues(workout) {
  const issues = [];
  const add = (code, message, extra = {}) => issues.push({code, message, workoutId: workout && workout.id || "", ...extra});
  if (!record(workout) || !Array.isArray(workout.groups) || !Array.isArray(workout.exercises)) {
    add("invalid-workout", "Sessione: esercizi o gruppi non validi.");
    return issues;
  }
  const groups = new Map();
  workout.groups.forEach((group) => {
    if (!record(group) || typeof group.id !== "string" || !group.id || groups.has(group.id)) {
      add("invalid-group", "Il gruppo è invalido o ha un identificativo duplicato.", {groupId: group && group.id || ""});
      return;
    }
    const name = group.name || "Gruppo";
    groups.set(group.id, {group, positions: [], rounds: groupRoundCount(group.rounds)});
    if (!["circuit", "superset"].includes(group.type)) add("invalid-group-type", `${name}: tipo di gruppo non valido.`, {groupId: group.id});
    if (groupRoundCount(group.rounds) === null) add("invalid-rounds", `${name}: imposta da 1 a ${MAX_GROUP_ROUNDS} giri.`, {groupId: group.id});
  });
  workout.exercises.forEach((exercise, index) => {
    if (!record(exercise) || !exercise.groupId) return;
    const entry = groups.get(exercise.groupId);
    if (!entry) {
      add("missing-group", `${exercise.name || "Esercizio"}: il gruppo non esiste nella seduta.`, {groupId: exercise.groupId, exerciseId: exercise.id});
      return;
    }
    entry.positions.push(index);
    if (!Array.isArray(exercise.sets) || !exercise.sets.length || exercise.sets.some((set) => !record(set))) {
      add("invalid-sets", `${exercise.name || "Esercizio"}: prescrizioni dei giri non valide.`, {groupId: exercise.groupId, exerciseId: exercise.id});
      return;
    }
    if (entry.rounds !== null && exercise.sets.length !== entry.rounds) {
      add("round-count-mismatch", `${exercise.name || "Esercizio"}: ${exercise.sets.length} prescrizioni per ${entry.rounds} giri; modifica i giri sul gruppo.`,
          {groupId: exercise.groupId, exerciseId: exercise.id, expected: entry.rounds, actual: exercise.sets.length,
            identical: samePrescriptions(exercise.sets)});
    }
  });
  groups.forEach(({group, positions}) => {
    if (positions.length < 2) add("group-members", `${group.name || "Gruppo"}: servono almeno due esercizi.`, {groupId: group.id});
    if (positions.length > 1 && positions[positions.length - 1] - positions[0] + 1 !== positions.length) {
      add("nonconsecutive-members", `${group.name || "Gruppo"}: gli esercizi devono essere consecutivi.`, {groupId: group.id});
    }
  });
  return issues;
}

function generatedSetId(exercise, group, index, ids) {
  const stem = `${exercise.id || group.id || "group"}`.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 100);
  const prefix = `${stem}_round_${index + 1}`;
  let id = prefix;
  for (let suffix = 1; ids.has(id); suffix++) id = `${prefix}_${suffix}`;
  ids.add(id);
  return id;
}

function normalizeWorkoutGroups(workout, {mode = "safe"} = {}) {
  if (!["safe", "rounds"].includes(mode)) throw new TypeError("Unknown training group normalization mode.");
  const issues = groupIssues(workout);
  if (!record(workout) || !Array.isArray(workout.groups) || !Array.isArray(workout.exercises)) return {workout, issues, changed: false};
  const groups = new Map(workout.groups.filter(record).map((group) => [group.id, group]));
  const blocked = new Set(issues.filter((issue) => issue.code !== "round-count-mismatch").map((issue) => issue.groupId));
  const ids = new Set(workout.exercises.flatMap((exercise) => Array.isArray(exercise && exercise.sets) ? exercise.sets.map((set) => set && set.id) : []));
  let changed = false;
  const exercises = workout.exercises.map((exercise) => {
    if (!record(exercise) || !exercise.groupId || blocked.has(exercise.groupId)) return exercise;
    const group = groups.get(exercise.groupId);
    const rounds = groupRoundCount(group && group.rounds);
    const sets = exercise.sets;
    if (rounds === null || !Array.isArray(sets) || !sets.length || sets.some((set) => !record(set)) || sets.length === rounds) return exercise;
    const identical = samePrescriptions(sets);
    // Safe migration discards only exact repetitions, never different prescriptions.
    if (mode === "safe" && (!identical || sets.length < rounds)) return exercise;
    // Read compatibility follows the authoritative rounds. Missing varying values remain unresolved.
    if (sets.length < rounds && !identical) return exercise;
    let nextSets = sets.slice(0, rounds);
    if (sets.length < rounds) {
      nextSets = sets.slice();
      for (let index = sets.length; index < rounds; index++) {
        nextSets.push({...sets[sets.length - 1], id: generatedSetId(exercise, group, index, ids)});
      }
    }
    changed = true;
    return {...exercise, sets: nextSets};
  });
  const next = changed ? {...workout, exercises} : workout;
  const remaining = groupIssues(next);
  return {workout: next, changed, issues: issues.map((issue) => ({...issue,
    resolved: !remaining.some((item) => item.code === issue.code && item.groupId === issue.groupId && item.exerciseId === issue.exerciseId)}))};
}

function normalizePlanGroups(plan, {mode = "safe"} = {}) {
  if (!record(plan) || !Array.isArray(plan.workouts)) return {plan, issues: [{code: "invalid-plan", message: "Programma: sedute non valide.", resolved: false}], changed: false};
  let changed = false;
  const issues = [];
  const workouts = plan.workouts.map((workout) => {
    const result = normalizeWorkoutGroups(workout, {mode});
    changed = changed || result.changed;
    issues.push(...result.issues);
    return result.workout;
  });
  return {plan: changed ? {...plan, workouts} : plan, issues, changed};
}

function validateGroupStructure(workouts) {
  if (!Array.isArray(workouts)) return ["Le sedute del programma non sono valide."];
  return workouts.flatMap((workout, index) => groupIssues(workout).map((issue) => `Seduta ${index + 1}: ${issue.message}`));
}

module.exports = {MAX_GROUP_ROUNDS, groupRoundCount, groupIssues, samePrescriptions,
  normalizeWorkoutGroups, normalizePlanGroups, validateGroupStructure};
